import { isServer } from '@/types/common';
import axios, { AxiosAdapter, AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance } from 'axios';

// 원본 axios adapter 보존
const originalAdapter = axios.defaults.adapter as AxiosAdapter;

/**
 * DNS 캐싱을 사용하는 커스텀 Axios Adapter 생성
 *
 * 🔥 서버사이드 전용 DNS 캐싱:
 * - cacheable-lookup 기반 고성능 DNS 캐싱 (Node.js 환경에서만)
 * - 클라이언트에서는 원본 adapter 사용 (브라우저 자체 DNS 캐싱)
 *
 * 서버사이드 기능:
 * 1. HTTP 요청 전에 호스트명을 캐싱된 IP로 자동 치환
 * 2. 수동 Round-robin으로 ALB 로드밸런싱 지원
 * 3. Host 헤더 유지로 ALB 라우팅 보장
 * 4. DNS 실패 시 원본 adapter로 fallback
 * 5. TTL 기반 자동 캐시 관리
 *
 * @returns AxiosAdapter DNS 캐싱이 적용된 adapter (서버사이드만)
 */
export const createDNSCacheAdapter = (): AxiosAdapter => {
  return async (config: AxiosRequestConfig) => {
    // 클라이언트사이드에서는 DNS 캐싱 비활성화
    if (!isServer) {
      // 브라우저에서는 원본 adapter 사용 (브라우저 자체 DNS 캐싱)
      return originalAdapter(config);
    }

    // baseURL이 없으면 원본 adapter 사용
    if (!config.baseURL) {
      return originalAdapter(config);
    }

    try {
      const url = new URL(config.baseURL);
      const hostname = url.hostname;

      // 이미 IP 주소인 경우 DNS 캐싱 생략
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (ipv4Regex.test(hostname)) {
        return originalAdapter(config);
      }

      // 서버사이드에서만 DNS 캐싱으로 IP 주소 해석 (Round-robin 자동 적용)
      const { dnsManager } = await import('./dnsCache');
      const dnsResult = await dnsManager.resolveWithDetailedInfo(hostname);

      // 호스트명을 해석된 IP로 치환
      const modifiedBaseURL = config.baseURL.replace(hostname, dnsResult.ip);
      const modifiedConfig = {
        ...config,
        baseURL: modifiedBaseURL,
        headers: {
          ...config.headers,
          Host: hostname, // ALB 라우팅을 위해 원본 호스트명 유지
        },
      };

      return originalAdapter(modifiedConfig);
    } catch (error) {
      console.error('DNS cache adapter error (server-side):', error);
      // DNS 실패 시 원본 adapter로 fallback
      return originalAdapter(config);
    }
  };
};

/**
 * DNS 캐싱 에러 핸들링 설정 (서버사이드 전용)
 *
 * 주요 기능:
 * 1. 네트워크 에러 감지 시 다음 IP로 자동 재시도 (서버에서만)
 * 2. DNS failover 로직으로 가용성 향상
 * 3. ALB IP 변경 시 무중단 서비스 보장
 *
 * @param axiosInstance DNS 캐싱을 적용할 axios 인스턴스
 */
export const setupDNSCacheErrorHandling = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const config = error.config;

      // 클라이언트사이드에서는 에러 핸들링 비활성화
      if (!isServer) {
        return Promise.reject(error);
      }

      // 네트워크 관련 에러 감지 시 DNS failover (서버사이드만)
      if (
        error.code === 'ECONNREFUSED' ||
        error.code === 'ENOTFOUND' ||
        error.code === 'ETIMEDOUT' ||
        error.code === 'EHOSTUNREACH'
      ) {
        const baseURL = config.baseURL || config.url;
        if (!baseURL) {
          return Promise.reject(error);
        }

        const url = new URL(baseURL);
        const hostname = url.hostname;

        // 재시도 횟수 제한 (1회만)
        const configWithRetry = config as AxiosRequestConfig & { __retryCount?: number };
        if (!configWithRetry.__retryCount) {
          configWithRetry.__retryCount = 1;

          try {
            // 서버사이드에서만 Round-robin에 의해 다음 IP로 자동 전환
            const { dnsManager } = await import('./dnsCache');
            const nextIP = await dnsManager.getNextIP(hostname);
            const retryBaseURL = baseURL.replace(url.hostname, nextIP);

            return axiosInstance({
              ...configWithRetry,
              baseURL: retryBaseURL,
            });
          } catch {
            return Promise.reject(error);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};
